"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useSensorEvents = void 0;
const react_1 = require("react");
const useLogger_1 = require("./useLogger");
const mutations_1 = require("../graphql/mutations");
const queries_1 = require("../graphql/queries");
const subscriptions_1 = require("../graphql/subscriptions");
const types_1 = require("../types");
const amplifyBootstrap_1 = require("../utils/amplifyBootstrap");
const useSensorEvents = (hubId, sensorId) => {
    const logger = (0, useLogger_1.useLogger)('useSensorEvents');
    const [sensorEvents, setSensorEvents] = (0, react_1.useState)([]);
    const [loading, setLoading] = (0, react_1.useState)(false);
    const [error, setError] = (0, react_1.useState)(null);
    const subscriptionsRef = (0, react_1.useRef)([]);
    // Guards against a slower fetch for a previous sensorId/hubId overwriting the
    // current one's events after a rapid switch.
    const eventsFetchSeqRef = (0, react_1.useRef)(0);
    const fetchSensorEventsBySensor = (0, react_1.useCallback)(async (sensorId, limit = 50) => {
        const fetchId = ++eventsFetchSeqRef.current;
        setLoading(true);
        setError(null);
        try {
            const response = await amplifyBootstrap_1.API.graphql({
                query: queries_1.getSensorEventsBySensor,
                variables: { sensorId, limit },
            });
            if (fetchId !== eventsFetchSeqRef.current)
                return;
            const sensorEvents = response.data?.getSensorEventsBySensor?.items ||
                response.data?.getSensorEventsBySensor ||
                [];
            const parsedSensorEvents = sensorEvents.map(parseSensorEvent);
            setSensorEvents(parsedSensorEvents);
        }
        catch (error) {
            logger.error('Error fetching sensor events:', { error });
            if (fetchId !== eventsFetchSeqRef.current)
                return;
            setError(error);
            setSensorEvents([]);
        }
        finally {
            if (fetchId === eventsFetchSeqRef.current)
                setLoading(false);
        }
    }, []);
    const fetchSensorEventsByHub = (0, react_1.useCallback)(async (hubId, limit = 50) => {
        const fetchId = ++eventsFetchSeqRef.current;
        setLoading(true);
        setError(null);
        try {
            const response = await amplifyBootstrap_1.API.graphql({
                query: queries_1.getSensorEventsByHub,
                variables: { hubId, limit },
            });
            if (fetchId !== eventsFetchSeqRef.current)
                return;
            const sensorEvents = response.data?.getSensorEventsByHub?.items || response.data?.getSensorEventsByHub || [];
            const parsedSensorEvents = sensorEvents.map(parseSensorEvent);
            setSensorEvents(parsedSensorEvents);
        }
        catch (error) {
            logger.error('Error fetching sensor events:', { error });
            if (fetchId !== eventsFetchSeqRef.current)
                return;
            setError(error);
            setSensorEvents([]);
        }
        finally {
            if (fetchId === eventsFetchSeqRef.current)
                setLoading(false);
        }
    }, []);
    const addSensorEvent = (0, react_1.useCallback)(async (input) => {
        try {
            const response = await amplifyBootstrap_1.API.graphql({
                query: mutations_1.createSensorEvent,
                variables: { input },
            });
            return response.data.createSensorEvent;
        }
        catch (error) {
            logger.error('Error creating sensor event:', { error });
            throw error;
        }
    }, []);
    const fetchSensorCurrentState = (0, react_1.useCallback)(async (sensorId) => {
        try {
            const response = await amplifyBootstrap_1.API.graphql({
                query: queries_1.getSensorCurrentState,
                variables: { sensorId },
            });
            return response.data.getSensorCurrentState;
        }
        catch (error) {
            logger.error('Error fetching sensor current state:', { error });
            throw error;
        }
    }, []);
    const fetchSensorHistoricalState = (0, react_1.useCallback)(async (sensorId, startTime, endTime, rollup) => {
        try {
            const response = await amplifyBootstrap_1.API.graphql({
                query: queries_1.getSensorHistoricalState,
                variables: {
                    sensorId,
                    startTime,
                    endTime,
                    rollup,
                },
            });
            const sensorEvents = response.data?.getSensorHistoricalState?.items ||
                response.data?.getSensorHistoricalState ||
                [];
            const parsedSensorEvents = sensorEvents.map(parseSensorEvent);
            return parsedSensorEvents;
        }
        catch (error) {
            logger.error('Error fetching sensor historical state:', { error });
            throw error;
        }
    }, []);
    const clear = () => {
        setSensorEvents([]);
    };
    const fetchAndSubscribe = (0, react_1.useCallback)(() => {
        if (sensorId) {
            fetchSensorEventsBySensor(sensorId);
            logger.debug(`subscribing to sensor events for sensorId: ${sensorId}`);
            try {
                const createSubscription = amplifyBootstrap_1.API.graphql({
                    query: subscriptions_1.onCreateSensorEventBySensor,
                    variables: { sensorId },
                }).subscribe({
                    next: ({ data }) => {
                        const newEvent = data.onSensorSensorEventCreated;
                        if (newEvent) {
                            setSensorEvents((prevEvents) => {
                                const updatedEvents = [parseSensorEvent(newEvent), ...prevEvents];
                                return updatedEvents.slice(0, 100);
                            });
                        }
                    },
                    error: (error) => logger.error('Create subscription error:', { error }),
                });
                subscriptionsRef.current = [createSubscription];
            }
            catch (error) {
                logger.error(`Error setting up subscriptions for sensor ID ${sensorId}`, { error });
            }
            return;
        }
        if (hubId) {
            fetchSensorEventsByHub(hubId);
            logger.debug(`subscribing to sensor events for hubId: ${hubId}`);
            try {
                const createSubscription = amplifyBootstrap_1.API.graphql({
                    query: subscriptions_1.onCreateSensorEventByHub,
                    variables: { hubId },
                }).subscribe({
                    next: ({ data }) => {
                        const newEvent = data.onHubSensorEventCreated;
                        if (newEvent) {
                            setSensorEvents((prevEvents) => {
                                const updatedEvents = [parseSensorEvent(newEvent), ...prevEvents];
                                return updatedEvents.slice(0, 100);
                            });
                        }
                    },
                    error: (error) => logger.error('Create subscription error:', { error }),
                });
                subscriptionsRef.current = [createSubscription];
            }
            catch (error) {
                logger.error(`Error setting up subscriptions for hub ID ${hubId}:`, { error });
            }
            return;
        }
        logger.debug(`subscribing to all sensor events`);
        try {
            const createSubscription = amplifyBootstrap_1.API.graphql({
                query: subscriptions_1.onCreateSensorEvent,
            }).subscribe({
                next: ({ data }) => {
                    const newEvent = data.onSensorEventCreated;
                    if (newEvent) {
                        setSensorEvents((prevEvents) => {
                            const updatedEvents = [parseSensorEvent(newEvent), ...prevEvents];
                            return updatedEvents.slice(0, 100);
                        });
                    }
                },
                error: (error) => logger.error('Create subscription error:', { error }),
            });
            subscriptionsRef.current = [createSubscription];
        }
        catch (error) {
            logger.error('Error setting up subscriptions:', { error });
        }
    }, [hubId, sensorId, fetchSensorEventsBySensor, fetchSensorEventsByHub]);
    const unsubscribe = (0, react_1.useCallback)(() => {
        if (subscriptionsRef.current.length === 0) {
            return;
        }
        try {
            subscriptionsRef.current.forEach((subscription) => {
                if (subscription && typeof subscription.unsubscribe === 'function') {
                    subscription.unsubscribe();
                }
            });
        }
        catch (error) {
            logger.error('Error cleaning up subscriptions:', { error });
        }
    }, []);
    const parsePressureValue = (0, react_1.useCallback)((value) => {
        if (value && typeof value === 'number') {
            const maxTargetPressure = 1.0; // Should be taken from config, mocked for now
            const minStaticPressure = 0.1; // Should be taken from config, mocked for now
            const possibleChange = maxTargetPressure - minStaticPressure;
            if (value > possibleChange || value < 0) {
                return 0;
            }
            const change = possibleChange - value;
            const relativeChange = change / possibleChange;
            return Math.round(relativeChange * 100); // Convert to percentage
        }
    }, []);
    const convertTemperatureValue = (0, react_1.useCallback)((value) => {
        const convertToFahrenheit = (value) => Math.round((value * 9) / 5 + 32); // (°C * 9/5) + 32
        if (!value)
            return undefined;
        if (typeof value === 'number') {
            return convertToFahrenheit(value);
        }
        if (typeof value === 'string') {
            const parsedValue = parseFloat(value);
            if (!isNaN(parsedValue)) {
                return convertToFahrenheit(parsedValue);
            }
        }
    }, []);
    const parseSensorEvent = (0, react_1.useCallback)((sensorEvent) => {
        const parsedSensorEvent = { ...sensorEvent };
        if (sensorEvent.metadata && typeof sensorEvent.metadata === 'string') {
            try {
                parsedSensorEvent.parsedMetadata = JSON.parse(sensorEvent.metadata);
            }
            catch (error) {
                logger.error(`Error parsing metadata for sensor event ${sensorEvent.id}:`, { error });
            }
        }
        if (sensorEvent.values && typeof sensorEvent.values === 'string') {
            try {
                const parsedValues = JSON.parse(sensorEvent.values);
                if (!parsedValues) {
                    logger.error(`Error parsing values for sensor event ${sensorEvent.id}: values is empty`);
                    parsedSensorEvent.values = sensorEvent.values;
                    return parsedSensorEvent;
                }
                if (parsedSensorEvent.parsedMetadata?.measurementType &&
                    parsedSensorEvent.parsedMetadata.measurementType === types_1.SensorType.HVAC_AIRHANDLER_MONITOR &&
                    parsedValues.pressure) {
                    const parsedPressure = parsePressureValue(parsedValues.pressure);
                    if (parsedPressure !== undefined) {
                        parsedValues.percentPressure = parsedPressure;
                    }
                }
                if (parsedValues.temperature) {
                    const convertedTemperature = convertTemperatureValue(parsedValues.temperature);
                    if (convertedTemperature !== undefined) {
                        parsedValues.temperature = convertedTemperature;
                    }
                }
                parsedSensorEvent.values = parsedValues;
                if (parsedSensorEvent.parsedMetadata?.measurementType === types_1.SensorType.POWER) {
                    const formattedValues = {};
                    // Voltage transformers
                    if (parsedValues.vt1 !== undefined) {
                        formattedValues.vt1 = {
                            value: parsedValues.vt1,
                            unit: 'V',
                            label: 'Voltage 1',
                        };
                    }
                    if (parsedValues.vt2 !== undefined) {
                        formattedValues.vt2 = {
                            value: parsedValues.vt2,
                            unit: 'V',
                            label: 'Voltage 2',
                        };
                    }
                    // Main current transformers
                    if (parsedValues.main_ct1 !== undefined) {
                        formattedValues.main_ct1 = {
                            value: parsedValues.main_ct1,
                            unit: 'A',
                            label: 'Main CT 1',
                        };
                    }
                    if (parsedValues.main_ct2 !== undefined) {
                        formattedValues.main_ct2 = {
                            value: parsedValues.main_ct2,
                            unit: 'A',
                            label: 'Main CT 2',
                        };
                    }
                    // Branch current transformers
                    for (let i = 1; i <= 14; i++) {
                        const key = `branch_ct${i}`;
                        if (parsedValues[key] !== undefined) {
                            formattedValues[key] = {
                                value: parsedValues[key],
                                unit: 'A',
                                label: `Branch CT ${i}`,
                            };
                        }
                    }
                    parsedSensorEvent.formattedValues = formattedValues;
                }
            }
            catch (error) {
                logger.error(`Error parsing values for sensor event ${sensorEvent.id}:`, { error });
                parsedSensorEvent.values = sensorEvent.values;
            }
        }
        return parsedSensorEvent;
    }, []);
    (0, react_1.useEffect)(() => {
        unsubscribe();
        fetchAndSubscribe();
        return () => {
            unsubscribe();
        };
    }, [hubId, sensorId]);
    return {
        sensorEvents,
        loading,
        error,
        fetchSensorEventsBySensor,
        fetchSensorEventsByHub,
        fetchSensorCurrentState,
        fetchSensorHistoricalState,
        addSensorEvent,
        clear,
    };
};
exports.useSensorEvents = useSensorEvents;
